import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { store } from '../..';
import { GradeSchoolClass, SchoolClass, SchoolClassSelection } from '@/models/school-class/school-class';
import { Student } from '@/models/student/student';
import { GEnum } from '@/constants/enum.defination';
import ExamSelectionType = GEnum.ExamineeSelectionType;
import { Competency, CompetencySelection } from '@/models/competency/competency';
import { studentService } from '@/service/api/student.service';
import { EvalSetting, EvalSettingDetailRequest } from '@/models/evalSetting/eval-setting';
import { evalSettingService } from '@/service/api/eval-setting.service';
import FileSaver from 'file-saver';
import moment from 'moment';
import i18n from '@/i18n';

export interface IExamCreateState {
  currentStep: number; // 現在の登録ステップ
  mode: number; // 更新モード(1:登録,2:編集,3:コピー)
  examineeSelectionType: ExamSelectionType; // 受検対象者タイプ(1:クラス,2:生徒)
  selectionSchoolClasses: SchoolClassSelection[]; // チェックボックスグループで選択されたクラス一覧
  examineeStudents: Student[]; // 選択された学生一覧
  selectionCognitive: CompetencySelection; // コンピテンシータイプ「認知」のチェックボックスグループ
  selectionSelf: CompetencySelection; // コンピテンシータイプ「自己」のチェックボックスグループ
  selectionGift: CompetencySelection; // コンピテンシータイプ「他者」のチェックボックスグループ
  selectionCommunity: CompetencySelection; // コンピテンシータイプ「コミュニティ」のチェックボックスグループ
  visitConfirmView: boolean; // 確認画面到達フラグ
  visitQuestionSettingView: boolean; // 確認画面到達フラグ
  visitExamineeSettingView: boolean; // 受検者設定画面到達フラグ
  examineeSchoolClassIDs: number[]; // 受検クラスIDリスト
  examineeStudentIDs: number[]; // 受検生徒IDリスト
  alreadyAssignedStudentIDs: Set<number>; // すでに受検者として割り当てられている生徒
  isAutoPageTransition: boolean; // 受検設定登録または更新後の自動遷移確認フラグ
}
@Module({ dynamic: true, store, name: 'examCreateModule', namespaced: true })
class ExamCreateModule extends VuexModule implements IExamCreateState {
  currentStep: number = 0;
  mode: number = 0;
  examineeSelectionType: ExamSelectionType = GEnum.ExamineeSelectionType.Class;
  selectionSchoolClasses: SchoolClassSelection[] = [];
  examineeStudents: Student[] = [];
  selectionCognitive: CompetencySelection = new CompetencySelection(GEnum.CompetencyType.Cognitive);
  selectionSelf: CompetencySelection = new CompetencySelection(GEnum.CompetencyType.Self);
  selectionGift: CompetencySelection = new CompetencySelection(GEnum.CompetencyType.Gift);
  selectionCommunity: CompetencySelection = new CompetencySelection(GEnum.CompetencyType.Community);
  visitConfirmView: boolean = false;
  visitQuestionSettingView: boolean = false;
  visitExamineeSettingView: boolean = false;
  examineeSchoolClassIDs: number[] = [];
  examineeStudentIDs: number[] = [];
  alreadyAssignedStudentIDs: Set<number> = new Set<number>();
  isAutoPageTransition: boolean = false;

  /**
   * 受検編集時に追加登録される生徒一覧(ただし、受検作成時はalreadyAssignedStudentIDsが空のため、assignedExamineeStudentsと同等の結果が得られる)
   */
  get plusExamineeStudents(): Student[] {
    return this.assignedExamineeStudents.filter((student: Student) => !examCreateModule.alreadyAssignedStudentIDs.has(student.studentID));
  }

  /**
   * 受検登録時に登録される生徒一覧
   */
  get assignedExamineeStudents(): Student[] {
    return this.examineeStudents.filter((student: Student) => !student.selected);
  }

  get examineeSelectionTypeString(): string {
    switch (this.examineeSelectionType) {
      case GEnum.ExamineeSelectionType.Class:
        return i18n.t('BY_SCHOOL_CLASS').toString();
      case GEnum.ExamineeSelectionType.Student:
        return i18n.t('EXAMINEE_SELECT_TYPE_STUDENT').toString();
    }
  }

  get evalSettingModeCreate(): boolean {
    return this.mode === GEnum.EvalSettingMode.Create;
  }

  get evalSettingModeEdit(): boolean {
    return this.mode === GEnum.EvalSettingMode.Edit;
  }

  get evalSettingModeCopy(): boolean {
    return this.mode === GEnum.EvalSettingMode.Copy;
  }

  get isAssignStudent(): boolean {
    return this.examineeSelectionType === GEnum.ExamineeSelectionType.Student;
  }

  get isAssignSchoolClass(): boolean {
    return this.examineeSelectionType === GEnum.ExamineeSelectionType.Class;
  }

  get selectedSchoolClassIDs(): number[] {
    return this.selectionSchoolClasses.map((selection: SchoolClassSelection) => selection.checkAllGroup).flat();
  }

  get selectedCompetencyIDs(): number[] {
    return [this.selectionCognitive.checkAllGroup, this.selectionSelf.checkAllGroup, this.selectionGift.checkAllGroup, this.selectionCommunity.checkAllGroup].flat();
  }

  @Mutation
  clean() {
    this.examineeSelectionType = GEnum.ExamineeSelectionType.Class;
    this.examineeStudents = [];
    this.visitConfirmView = false;
    this.visitQuestionSettingView = false;
    this.visitExamineeSettingView = false;
    this.examineeSchoolClassIDs = [];
    this.examineeStudentIDs = [];
    this.alreadyAssignedStudentIDs = new Set<number>();
    this.currentStep = 0;
    this.isAutoPageTransition = false;
  }

  @Mutation
  setAlreadyAssignedStudentIDs(students: Student[]) {
    this.alreadyAssignedStudentIDs = students.reduce((result: Set<number>, student: Student) => {
      result.add(student.studentID);
      return result;
    }, new Set<number>());
  }

  @Mutation
  setVisitConfirmView(value: boolean) {
    this.visitConfirmView = value;
  }

  @Mutation
  setVisitQuestionSettingView(value: boolean) {
    this.visitQuestionSettingView = value;
  }

  @Mutation
  setVisitExamineeSettingView(value: boolean) {
    this.visitExamineeSettingView = value;
  }

  @Mutation
  setIsAutoPageTransition(value: boolean) {
    this.isAutoPageTransition = value;
  }

  /**
   * コンピテンシー用のcheckbox groupにラベル設定し、初期化する
   */
  @Mutation
  cleanCompetencySelections() {
    this.selectionCognitive = new CompetencySelection(GEnum.CompetencyType.Cognitive);
    this.selectionSelf = new CompetencySelection(GEnum.CompetencyType.Self);
    this.selectionGift = new CompetencySelection(GEnum.CompetencyType.Gift);
    this.selectionCommunity = new CompetencySelection(GEnum.CompetencyType.Community);
  }

  /**
   * クラス用のcheckbox groupにチェック値を反映する
   * @param schoolClasses 選択されたクラス
   */
  @Mutation
  setSchoolClassSelection(schoolClasses: SchoolClass[]) {
    this.selectionSchoolClasses.forEach((selectionSchoolClass: SchoolClassSelection) => {
      selectionSchoolClass.setCheckAllGroup(schoolClasses.filter((schoolClass: SchoolClass) => selectionSchoolClass.grade === schoolClass.grade));
    });
  }

  /**
   * コンピテンシー用のcheckbox groupにチェック値を反映する
   * @param competencies 選択されたコンピテンシー
   */
  @Mutation
  setCompetencySelections(competencies: Competency[]) {
    this.selectionCognitive.setCheckGroup(
      competencies.filter((competency: Competency) => competency.competencyType === GEnum.CompetencyType.Cognitive).map((competency: Competency) => competency.competencyID)
    );
    this.selectionSelf.setCheckGroup(competencies.filter((competency: Competency) => competency.competencyType === GEnum.CompetencyType.Self).map((competency: Competency) => competency.competencyID));
    this.selectionGift.setCheckGroup(competencies.filter((competency: Competency) => competency.competencyType === GEnum.CompetencyType.Gift).map((competency: Competency) => competency.competencyID));
    this.selectionCommunity.setCheckGroup(
      competencies.filter((competency: Competency) => competency.competencyType === GEnum.CompetencyType.Community).map((competency: Competency) => competency.competencyID)
    );
  }

  @Mutation
  setExamineeSelectionType(value: GEnum.ExamineeSelectionType) {
    this.examineeSelectionType = value;
  }

  @Mutation
  setSelectionCognitive(value: CompetencySelection) {
    this.selectionCognitive = value;
  }
  @Mutation
  setSelectionSelf(value: CompetencySelection) {
    this.selectionSelf = value;
  }
  @Mutation
  setSelectionGift(value: CompetencySelection) {
    this.selectionGift = value;
  }
  @Mutation
  setSelectionCommunity(value: CompetencySelection) {
    this.selectionCommunity = value;
  }

  @Mutation
  setMode(value: number) {
    this.mode = value;
  }

  @Mutation
  setExamnieeSelectionType(value: GEnum.ExamineeSelectionType) {
    this.examineeSelectionType = value;
  }

  @Mutation
  setExamineeStudents(value: Student[]) {
    this.examineeStudents = value;
  }

  @Mutation
  toggleSelectForStudents(index: number) {
    this.examineeStudents[index].selected = !this.examineeStudents[index].selected;
  }

  @Mutation
  setCurrentStep(value: number) {
    this.currentStep = value;
  }

  @Mutation
  setExamineeStudentIDs(value: number[]) {
    this.examineeStudentIDs = value;
  }

  @Mutation
  setExamineeSchoolClassIDs(value: number[]) {
    this.examineeSchoolClassIDs = value;
  }

  @Mutation
  resetSchoolClassSelection(gradeSchoolClasses: GradeSchoolClass[]) {
    this.selectionSchoolClasses = [];
    gradeSchoolClasses.forEach((v: GradeSchoolClass, _: number) => {
      this.selectionSchoolClasses.push(new SchoolClassSelection(v.grade));
    });
  }

  @Mutation
  mergeExamineeStudents(newStudents: Student[]) {
    let tempExamineeStudents: Set<number> = this.examineeStudents.reduce((result: Set<number>, student: Student) => {
      result.add(student.studentID);
      return result;
    }, new Set<number>());
    newStudents.forEach((student: Student) => {
      if (!tempExamineeStudents.has(student.studentID)) {
        this.examineeStudents.push(student);
      }
    });
  }

  @Mutation
  cleanSelectedStudents() {
    this.examineeStudents = [];
  }

  /**
   * 受検設定更新
   * @param updateEvalSettingDetail 受検設定更新情報
   */
  @Action({ rawError: true })
  async updateEvalSetting(updateEvalSettingDetail: EvalSetting) {
    let result: any;
    const request: EvalSettingDetailRequest = {
      competencyIDs: updateEvalSettingDetail.competencyIDs,
      competencySettingType: updateEvalSettingDetail.competencySettingType,
      deadline: moment(updateEvalSettingDetail.deadline).toISOString(),
      freeWordSkillRequired: updateEvalSettingDetail.freeWordSkillRequired,
      giftRequired: updateEvalSettingDetail.giftRequired,
      iatRequired: updateEvalSettingDetail.iatRequired,
      trendRequired: updateEvalSettingDetail.trendRequired,
      trendIDs: updateEvalSettingDetail.trendIDs,
      isProgressNotification: updateEvalSettingDetail.isProgressNotification,
      questionSettingType: updateEvalSettingDetail.questionSettingType,
      requiredRespondents: updateEvalSettingDetail.requiredRespondents,
      isEvaluatorVisible: updateEvalSettingDetail.isEvaluatorVisible,
      selfEvalRequired: updateEvalSettingDetail.selfEvalRequired,
      startTime: moment(updateEvalSettingDetail.startTime).toISOString(),
      title: updateEvalSettingDetail.title,
      titleLangs: updateEvalSettingDetail.titleLangs,
      isPostingReportEnabled: updateEvalSettingDetail.isPostingReportEnabled,
      evaluationRequestPermissionCategory: updateEvalSettingDetail.evaluationRequestPermissionCategory,
      personalReportDisplayType: updateEvalSettingDetail.personalReportDisplayType,
      mailSetting: updateEvalSettingDetail.mailSetting,
    } as EvalSettingDetailRequest;
    if (this.examineeSchoolClassIDs.length > 0) {
      //クラス別に受検コース登録
      request.schoolClassIDs = this.examineeSchoolClassIDs;
    } else {
      //生徒個別に受検コース登録
      request.studentIDs = this.examineeStudentIDs;
    }

    result = await evalSettingService.updateEvalSetting(updateEvalSettingDetail.evalSettingID, request);

    return result;
  }

  /**
   * 受検設定登録
   * @param newEvalSettingDetail 受検設定登録情報
   */
  @Action({ rawError: true })
  async entryEvalSetting(newEvalSettingDetail: EvalSetting) {
    let result: EvalSetting = new EvalSetting();
    const request: EvalSettingDetailRequest = {
      competencyIDs: newEvalSettingDetail.competencyIDs,
      competencySettingType: newEvalSettingDetail.competencySettingType,
      deadline: moment(newEvalSettingDetail.deadline).toISOString(),
      freeWordSkillRequired: newEvalSettingDetail.freeWordSkillRequired,
      giftRequired: newEvalSettingDetail.giftRequired,
      iatRequired: newEvalSettingDetail.iatRequired,
      trendRequired: newEvalSettingDetail.trendRequired,
      trendIDs: newEvalSettingDetail.trendIDs,
      isProgressNotification: newEvalSettingDetail.isProgressNotification,
      questionSettingType: newEvalSettingDetail.questionSettingType,
      requiredRespondents: newEvalSettingDetail.requiredRespondents,
      isEvaluatorVisible: newEvalSettingDetail.isEvaluatorVisible,
      selfEvalRequired: newEvalSettingDetail.selfEvalRequired,
      startTime: moment(newEvalSettingDetail.startTime).toISOString(),
      title: newEvalSettingDetail.title,
      titleLangs: newEvalSettingDetail.titleLangs,
      isPostingReportEnabled: newEvalSettingDetail.isPostingReportEnabled,
      evaluationRequestPermissionCategory: newEvalSettingDetail.evaluationRequestPermissionCategory,
      personalReportDisplayType: newEvalSettingDetail.personalReportDisplayType,
      mailSetting: newEvalSettingDetail.mailSetting,
    } as EvalSettingDetailRequest;

    if (this.examineeSchoolClassIDs.length > 0) {
      //クラス別に受検コース登録
      request.schoolClassIDs = this.examineeSchoolClassIDs;
      result = await evalSettingService.addEvalSettingForSchoolClasses(request);
    } else {
      //生徒個別に受検コース登録
      request.studentIDs = this.examineeStudentIDs;
      result = await evalSettingService.addEvalSettingForStudents(request);
    }
    return result;
  }

  /**
   * 生徒一覧エクセルロード
   * @param excelFileData 生徒一覧ダウンロードから取得したエクセルファイル
   */
  @Action({ rawError: true })
  async loadStudents(excelFileData: FormData) {
    const result: Student[] = await studentService.loadStudentExcelFile(excelFileData);
    if (result) {
      this.mergeExamineeStudents(result);
    }
  }

  /**
   * リクエストテンプレート ダウンロード
   * @param evalSettingID コースID
   */
  @Action({ rawError: true })
  async downloadRequestTemplate(evalSettingID: number) {
    const file = await evalSettingService.downloadRequestTemplate(evalSettingID);
    FileSaver.saveAs(file, '評価リクエストテンプレート.xlsx');
  }

  /**
   * リクエストテンプレート インポート
   * @param formData エクセルフォーム
   * @param evalSettingID コースID
   */
  @Action({ rawError: true })
  async importRequestTemplate({ formData, evalSettingID }: any) {
    return await evalSettingService.sendRequesetExcelFile(formData, evalSettingID);
  }
}

export const examCreateModule = getModule(ExamCreateModule);
